// Slideshow: images shown one at a time, filling the tile (data.fit 'cover', cropped) or whole ('contain'), changing
// every data.interval seconds (Speed) with a fade or a slide (data.effect, Transition); a single image stays still.
// Editors add images by dropping files (or an image dragged from another page) on the tile, Add Images in its menu (a
// file picker, several at once) or Add Image from URL. Each becomes one of the webtop's assets (putAsset, applets/storage/src/assets.js:
// kept by the server, or by the browser for a webtop stored there) and its address is added to the end of data.images
// with an ordinary write of the tile's data; Edit shows them as thumbnails to reorder (drag one, or its arrow buttons)
// or remove, and the store deletes what no tile shows. Every image is shown through assetSrc (an asset kept in this
// browser is read from its storage). Which image shows comes from the clock (whole multiples of the interval), so a
// redraw shows the same one and nothing is kept per tile; one heartbeat ticks every slideshow while any of them has
// more than one image. Only the image shown and the next one are loaded, so a long slideshow does not fetch every
// image at once.
import {
  api, assetSrc, choiceItem, currentRole, EDIT, editItem, el$, formDialog, heartbeat, lineIcon, putAsset, render, tilePath, toast,
  typedUrl, widgetNote,
} from './kit.js';

export default {
  kind: 'slideshow',
  label: 'Slideshow',
  icon: lineIcon('slideshow'),
  about: 'Your images, one after another, with a fade or a slide',
  look: 'bare',
  sizes: ['2x2', '1x1', '2x1', '3x1', '3x3'],
  create: () => ({ title: 'Slideshow', data: {} }), // store.newShortcut fills in the defaults
  render: renderSlides,
  menu: (s) => [
    { label: 'Add Images', roles: EDIT, action: () => pickImages(s) },
    { label: 'Add Image from URL', roles: EDIT, action: () => addFromUrl(s) },
    choiceItem(s, 'Speed', SPEEDS, 'interval', () => s.data?.interval ?? 5),
    choiceItem(s, 'Transition', [['fade', 'Fade'], ['slide', 'Slide']], 'effect', () => s.data?.effect ?? 'fade'),
    choiceItem(s, 'Scaling', [['cover', 'Fill'], ['contain', 'Fit']], 'fit', () => s.data?.fit ?? 'cover'),
    !!s.data?.images?.length && editItem(slidesDialog, s, fillSlides),
  ],
};

// data.interval's choices (model.js, SLIDE_INTERVALS, has the same seconds)
const SPEEDS = [[3, '3 seconds'], [5, '5 seconds'], [10, '10 seconds'], [30, '30 seconds'], [60, '1 minute']];
const images = (s) => s.data?.images ?? [];

function renderSlides(s, el) {
  const list = images(s);
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', `${s.title || 'Slideshow'}, ${list.length} image${list.length === 1 ? '' : 's'}`);
  el.dataset.effect = s.data?.effect === 'slide' ? 'slide' : 'fade';
  el.dataset.fit = s.data?.fit === 'contain' ? 'contain' : 'cover';
  el.dataset.interval = String(s.data?.interval ?? 5);
  const editor = currentRole() === 'editor';
  const body = el$('div', 'slides-body');
  el.append(body);
  if (editor) dropImages(el, s);
  if (!list.length) {
    body.append(widgetNote(editor ? 'Drop images here, or right-click › Add Images.' : 'No images yet.'));
    return;
  }
  for (const src of list) {
    const img = el$('img', 'slide');
    img.dataset.src = src;
    img.alt = '';
    img.draggable = false; // the pointer drags the tile, not the image
    img.decoding = 'async';
    body.append(img);
  }
  showSlide(el, false);
  el.classList.toggle('playing', list.length > 1);
  if (list.length > 1) ticking ??= heartbeat(tick);
}

// The image due now: the number of whole intervals since 1970, round the list.
function showSlide(el, animate) {
  const slides = el.querySelectorAll('.slide');
  if (!slides.length) return;
  const i = Math.floor(Date.now() / (Number(el.dataset.interval) * 1000)) % slides.length;
  const next = slides[i];
  load(next);
  load(slides[(i + 1) % slides.length]); // ready before its turn
  const prev = el.querySelector('.slide.shown');
  if (prev === next) return;
  for (const g of el.querySelectorAll('.slide.gone')) g.classList.remove('gone');
  if (!animate) { prev?.classList.remove('shown'); next.classList.add('shown'); return; }
  // An image that slid out to the left starts its next turn from the right: placed there with no transition first.
  void next.offsetWidth;
  prev?.classList.replace('shown', 'gone');
  next.classList.add('shown');
}
// Sets an image's src once (resolved through assetSrc: an asset kept in this browser comes from its storage).
function load(img) {
  if (img.dataset.loading) return;
  img.dataset.loading = '1';
  assetSrc(img.dataset.src).then((src) => { img.src = src; });
}

let ticking = null; // the heartbeat's unsubscribe while any slideshow plays
function tick() {
  const playing = document.querySelectorAll('#webtop .tile.slideshow.playing');
  if (!playing.length) { ticking?.(); ticking = null; return; }
  for (const el of playing) showSlide(el, true);
}

// ---- adding images
// `source` (a file, or an address) becomes one of the webtop's assets, then its address goes to the end of the
// slideshow's images with a write of the tile's data, which answers the data as saved.
async function addImage(s, source) {
  const { url } = await putAsset(source);
  s.data = await api('PATCH', tilePath(s, '/data'), { images: [...images(s), url] });
}
// One at a time, each saved before the next, so a failure keeps the ones before it.
async function upload(s, files) {
  const list = [...files].filter((f) => f.type.startsWith('image/'));
  if (!list.length) return toast('Pick image files', { kind: 'error' });
  try {
    for (const [n, f] of list.entries()) {
      toast(list.length > 1 ? `Uploading image ${n + 1} of ${list.length}…` : 'Uploading image…');
      await addImage(s, f);
    }
    toast(list.length > 1 ? `${list.length} images added` : 'Image added');
  } catch (e) { toast(e); }
  render();
}
async function fetchUrl(s, url) {
  toast('Fetching image…');
  try {
    await addImage(s, url);
    toast('Image added');
  } catch (e) { toast(e); }
  render();
}

// Files, or an image dragged from another page (its address: the <img> of its HTML, else the link it carries).
function dropImages(el, s) {
  const wanted = (e) => e.dataTransfer?.types.some((t) => t === 'Files' || t === 'text/uri-list');
  const over = (e) => {
    if (!wanted(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    el.classList.add('drop-over');
  };
  el.addEventListener('dragenter', over);
  el.addEventListener('dragover', over);
  el.addEventListener('dragleave', (e) => { if (!el.contains(e.relatedTarget)) el.classList.remove('drop-over'); });
  el.addEventListener('drop', (e) => {
    el.classList.remove('drop-over');
    if (!wanted(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files.length) return upload(s, e.dataTransfer.files);
    const html = e.dataTransfer.getData('text/html');
    const src = html && new DOMParser().parseFromString(html, 'text/html').querySelector('img')?.getAttribute('src');
    const link = e.dataTransfer.getData('text/uri-list').split(/\r?\n/).find((l) => l && !l.startsWith('#'));
    const url = [src, link].find((u) => /^https?:\/\//i.test(u ?? ''));
    if (url) fetchUrl(s, url);
    else toast('Drop an image file, or an image from another page', { kind: 'error' });
  });
}

let picker = null;
let pickingFor = null;
function pickImages(s) {
  if (!picker) {
    picker = Object.assign(document.createElement('input'), { type: 'file', accept: 'image/*', multiple: true, hidden: true });
    picker.addEventListener('change', () => {
      const files = [...picker.files];
      picker.value = '';
      if (pickingFor && files.length) upload(pickingFor, files);
    });
    document.body.append(picker);
  }
  pickingFor = s;
  picker.click();
}

// Add Image from URL: the server fetches it while the dialog waits, so an address it cannot use is said there.
let urlFor = null;
const urlDialog = formDialog('slideshow-url', {
  title: 'Add Image from URL',
  button: 'Add',
  fields: `
    <label>Image address
      <input type="text" name="url" required maxlength="2000" autocapitalize="off" autocorrect="off" spellcheck="false"
             placeholder="https://example.com/photo.jpg">
    </label>`,
  async check(f, busy) {
    const url = typedUrl(f.url.value, 'an image');
    busy();
    await addImage(urlFor, url);
    return true;
  },
});
async function addFromUrl(s) {
  urlFor = s;
  if (!(await urlDialog.open(() => {}))) return;
  render();
  toast('Image added');
}

// ---- Edit: the images as thumbnails, in order. Drag one (mouse, finger or pen) to move it, or use its arrows;
// Remove takes it out. Save keeps the new order.
const slidesDialog = formDialog('slideshow', {
  title: 'Slideshow Images',
  fields: '<ol class="slides-list"></ol><p class="muted slides-hint">Drag an image to move it.</p>',
  setup: (form) => reorderByDrag(form.querySelector('.slides-list')),
  check(f) {
    const list = slidesDialog.form.querySelector('.slides-list');
    return { data: { images: [...list.children].map((li) => li.dataset.src) } };
  },
});
function fillSlides(f, s) {
  const list = slidesDialog.form.querySelector('.slides-list');
  list.replaceChildren(...images(s).map(slideItem));
}
function slideItem(src) {
  const li = el$('li', 'slides-item');
  li.dataset.src = src;
  const img = el$('img');
  assetSrc(src).then((u) => { img.src = u; });
  img.alt = '';
  img.draggable = false;
  const button = (cls, label, icon, action) => {
    const b = el$('button', cls);
    b.type = 'button';
    b.title = label;
    b.setAttribute('aria-label', label);
    b.innerHTML = lineIcon(icon);
    b.addEventListener('click', action);
    return b;
  };
  li.append(img,
    button('slides-earlier', 'Move earlier', 'chevron', () => li.previousElementSibling?.before(li)),
    button('slides-later', 'Move later', 'chevron', () => li.nextElementSibling?.after(li)),
    button('slides-remove', 'Remove', 'trash', () => li.remove()));
  return li;
}
// Pointer events rather than HTML drag and drop, so a finger moves a thumbnail too: the one held goes before or after
// the thumbnail under the pointer, by which half of it the pointer is over.
function reorderByDrag(list) {
  list.addEventListener('pointerdown', (e) => {
    const li = e.target.closest('.slides-item');
    if (!li || e.target.closest('button') || e.button > 0) return;
    e.preventDefault();
    li.setPointerCapture(e.pointerId);
    li.classList.add('moving');
    const move = (ev) => {
      const over = document.elementFromPoint(ev.clientX, ev.clientY)?.closest('.slides-item');
      if (!over || over === li || over.parentNode !== list) return;
      const r = over.getBoundingClientRect();
      if (ev.clientX > r.left + r.width / 2) over.after(li); else over.before(li);
    };
    const done = () => {
      li.classList.remove('moving');
      li.removeEventListener('pointermove', move);
      li.removeEventListener('pointerup', done);
      li.removeEventListener('pointercancel', done);
    };
    li.addEventListener('pointermove', move);
    li.addEventListener('pointerup', done);
    li.addEventListener('pointercancel', done);
  });
}
